import { useEffect, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Field,
  Heading,
  Input,
  NativeSelect,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import {
  listDepartments,
  listMunicipalities,
  type Department,
  type Municipality,
} from '@/lib/api';
import { RequiredLabel, RequiredLegend } from '@/components/ui/required-field';

export type ProjectFormValues = {
  code: string;
  name: string;
  description: string;
  address: string;
  countryCode: string;
  departmentId: string;
  municipalityId: string;
};

export const EMPTY_PROJECT_FORM: ProjectFormValues = {
  code: '',
  name: '',
  description: '',
  address: '',
  countryCode: 'GT',
  departmentId: '',
  municipalityId: '',
};

type Props = {
  initial: ProjectFormValues;
  /** When true, `code` is read-only (code is immutable after creation). */
  lockCode?: boolean;
  submitLabel: string;
  submitting: boolean;
  error?: string | null;
  onSubmit: (values: ProjectFormValues) => void | Promise<void>;
  onCancel: () => void;
};

export function ProjectForm({
  initial,
  lockCode,
  submitLabel,
  submitting,
  error,
  onSubmit,
  onCancel,
}: Props) {
  const [values, setValues] = useState<ProjectFormValues>(initial);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [loadingMunis, setLoadingMunis] = useState(false);

  useEffect(() => {
    void listDepartments(values.countryCode).then(setDepartments).catch(() => setDepartments([]));
  }, [values.countryCode]);

  useEffect(() => {
    if (!values.departmentId) {
      setMunicipalities([]);
      return;
    }
    setLoadingMunis(true);
    listMunicipalities(values.departmentId)
      .then(setMunicipalities)
      .catch(() => setMunicipalities([]))
      .finally(() => setLoadingMunis(false));
  }, [values.departmentId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await onSubmit(values);
  }

  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
      <Heading size="md" mb="5">
        Datos del proyecto
      </Heading>
      <form onSubmit={handleSubmit}>
        <Stack gap="4">
          <Stack direction={{ base: 'column', md: 'row' }} gap="4" align="flex-start">
            <Field.Root flex={{ base: '1', md: '0 0 200px' }} required>
              <RequiredLabel>Código corto</RequiredLabel>
              <Input
                value={values.code}
                onChange={(e) =>
                  setValues({ ...values, code: e.currentTarget.value.toUpperCase() })
                }
                placeholder="ARB"
                maxLength={8}
                disabled={lockCode}
              />
              <Field.HelperText>2–8 caracteres, mayúsculas y números. Inmutable.</Field.HelperText>
            </Field.Root>
            <Field.Root flex="1" required>
              <RequiredLabel>Nombre</RequiredLabel>
              <Input
                value={values.name}
                onChange={(e) => setValues({ ...values, name: e.currentTarget.value })}
                placeholder="Arben Villa Nueva"
              />
            </Field.Root>
          </Stack>

          <Field.Root>
            <Field.Label>Descripción</Field.Label>
            <Textarea
              value={values.description}
              onChange={(e) => setValues({ ...values, description: e.currentTarget.value })}
              placeholder="Notas internas, ubicación detallada, etc."
              rows={3}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label>Dirección</Field.Label>
            <Input
              value={values.address}
              onChange={(e) => setValues({ ...values, address: e.currentTarget.value })}
              placeholder="Ubicación del proyecto / lotificación"
              maxLength={300}
            />
            <Field.HelperText>
              Se usa como dirección predeterminada al crear contratos (editable por contrato).
            </Field.HelperText>
          </Field.Root>

          <Stack direction={{ base: 'column', md: 'row' }} gap="4" align="flex-start">
            <Field.Root flex="1">
              <Field.Label>Departamento</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  value={values.departmentId}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      departmentId: e.currentTarget.value,
                      municipalityId: '',
                    })
                  }
                >
                  <option value="">— Seleccionar —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
            <Field.Root flex="1" disabled={!values.departmentId || loadingMunis}>
              <Field.Label>Municipio</Field.Label>
              <NativeSelect.Root disabled={!values.departmentId || loadingMunis}>
                <NativeSelect.Field
                  value={values.municipalityId}
                  onChange={(e) =>
                    setValues({ ...values, municipalityId: e.currentTarget.value })
                  }
                >
                  <option value="">
                    {loadingMunis ? 'Cargando…' : '— Seleccionar —'}
                  </option>
                  {municipalities.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          </Stack>

          {error && (
            <Text color="red.600" fontSize="sm">
              {error}
            </Text>
          )}

          <RequiredLegend />

          <Stack
            direction={{ base: 'column-reverse', md: 'row' }}
            gap="3"
            justify="flex-end"
          >
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={submitting}
              w={{ base: 'full', md: 'auto' }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              colorPalette="brand"
              bg="brand.600"
              color="white"
              _hover={{ bg: 'brand.700' }}
              loading={submitting}
              loadingText="Guardando…"
              w={{ base: 'full', md: 'auto' }}
            >
              {submitLabel}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}
